import { createHttpClient } from '@shared/adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { Input } from '@shared/components/ui/shadcn/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import { apiClientWH } from '@shared/services/ApiClient';
import { ArrowLeft, MapPin, Package, Plus, Tag, Trash2, Printer, X, Check } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryReceiptMovement } from '../../../models/Entry';
import { DispatchByLocationLine, PendingDispatchLocation, ReceiptApiAdapter } from '../../adapters/ReceiptApiAdapter';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag as faTagIcon, faBox } from '@fortawesome/pro-light-svg-icons';

const buildImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageUrl}`;
};

export interface DispatchModalRef {
  open: (
    locations: PendingDispatchLocation[],
    onConfirm: (
      locationId: number,
      dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
      lines?: DispatchByLocationLine[],
    ) => Promise<{ success: boolean }>,
    entryId: number,
    receiptId: number,
  ) => void;
}

type DispatchStep = 'select_type' | 'configure_groups' | 'print_labels';

export const DispatchModal = forwardRef<DispatchModalRef>((_, ref) => {
  const { t } = useTranslation('merchandiseV3');
  const modalRef = useRef<ModalCenterRef>(null);
  const [locations, setLocations] = useState<PendingDispatchLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [dispatchType, setDispatchType] = useState<'LOCATION_ORDER' | 'EXTRA_WORK_ORDER'>('LOCATION_ORDER');
  const [currentStep, setCurrentStep] = useState<DispatchStep>('select_type');
  const [isProcessing, setIsProcessing] = useState(false);
  const [entryId, setEntryId] = useState<number | null>(null);
  const [receiptId, setReceiptId] = useState<number | null>(null);
  const [movements, setMovements] = useState<EntryReceiptMovement[]>([]);
  const [isLoadingMovements, setIsLoadingMovements] = useState(false);
  const [lines, setLines] = useState<DispatchByLocationLine[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [printingGroupCode, setPrintingGroupCode] = useState<string | null>(null);
  const [printedGroupCodes, setPrintedGroupCodes] = useState<Set<string>>(new Set());
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewImageName, setPreviewImageName] = useState<string | null>(null);
  const confirmCallbackRef = useRef<
    | ((
        locationId: number,
        dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
        lines?: DispatchByLocationLine[],
      ) => Promise<{ success: boolean }>)
    | null
  >(null);

  useImperativeHandle(ref, () => ({
    open: (
      locationsData: PendingDispatchLocation[],
      onConfirm: (
        locationId: number,
        dispatchType: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER',
        lines?: DispatchByLocationLine[],
      ) => Promise<{ success: boolean }>,
      eId: number,
      rId: number,
    ) => {
      setLocations(locationsData);
      setSelectedLocationId(locationsData.length > 0 ? locationsData[0].location_id : null);
      setDispatchType('LOCATION_ORDER');
      setCurrentStep('select_type');
      setIsProcessing(false);
      setEntryId(eId);
      setReceiptId(rId);
      setLines([]);
      setValidationErrors([]);
      confirmCallbackRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  // Cargar movimientos cuando se selecciona EXTRA_WORK_ORDER y hay location_id
  useEffect(() => {
    if (dispatchType === 'EXTRA_WORK_ORDER' && selectedLocationId && entryId && receiptId) {
      loadMovements();
    }
  }, [dispatchType, selectedLocationId, entryId, receiptId]);

  const loadMovements = async () => {
    if (!selectedLocationId || !entryId || !receiptId) return;

    setIsLoadingMovements(true);
    try {
      const httpClient = createHttpClient(apiClientWH);
      const receiptAdapter = new ReceiptApiAdapter(httpClient);
      const movementsData = await receiptAdapter.getMovements(entryId, receiptId, { location_id: selectedLocationId });

      // Filtrar solo movimientos aceptados pendientes de despacho Y del carrito seleccionado
      const acceptedMovements = movementsData.filter(
        (m) =>
          m.movement_type === 'ACCEPTED' && m.dispatch_status === 'PENDING' && m.location_id === selectedLocationId,
      );
      setMovements(acceptedMovements);

      // No crear grupos por defecto - el usuario debe crearlos manualmente
      setLines([]);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
    } finally {
      setIsLoadingMovements(false);
    }
  };

  const handleDispatchTypeChange = (value: 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER') => {
    setDispatchType(value);
    if (value === 'LOCATION_ORDER') {
      setCurrentStep('select_type');
    }
  };

  const handleNextStep = () => {
    if (dispatchType === 'EXTRA_WORK_ORDER') {
      setCurrentStep('configure_groups');
    } else {
      handleConfirm();
    }
  };

  const handleBackToSelectType = () => {
    setCurrentStep('select_type');
  };

  // Generar código de grupo único con formato TE-AAMM-random
  const generateGroupCode = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // Últimos 2 dígitos del año
    const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Mes con 2 dígitos
    const random = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0'); // 4 dígitos aleatorios
    return `TE-${year}${month}-${random}`;
  };

  // Verificar si un código de grupo ya existe
  const isGroupCodeDuplicate = (groupCode: string, excludeIndex?: number): boolean => {
    if (!groupCode || !groupCode.trim()) return false;
    return lines.some(
      (line, index) =>
        index !== excludeIndex && line.group_code?.trim().toUpperCase() === groupCode.trim().toUpperCase(),
    );
  };

  const updateLine = (index: number, field: keyof DispatchByLocationLine, value: any) => {
    const newLines = [...lines];

    // Si se está actualizando el group_code, validar que no sea duplicado
    if (field === 'group_code' && value) {
      const trimmedValue = value.trim();
      if (trimmedValue && isGroupCodeDuplicate(trimmedValue, index)) {
        setValidationErrors([
          t('dispatch.modal.duplicate_group_code', 'Este código de grupo ya existe. Debe ser único.'),
        ]);
        return; // No actualizar si es duplicado
      }
    }

    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
    setValidationErrors([]);
  };

  const addLineForProduct = (productId: number) => {
    // Sumar todas las cantidades de movimientos de este producto
    const totalAvailableQty = movements.filter((m) => m.product_id === productId).reduce((sum, m) => sum + m.qty, 0);

    if (totalAvailableQty === 0) return;

    // Calcular cantidad ya asignada a otras líneas de este producto
    const assignedQty = lines.filter((l) => l.product_id === productId).reduce((sum, l) => sum + l.qty_expected, 0);

    const availableQty = totalAvailableQty - assignedQty;
    if (availableQty <= 0) return;

    // Generar código único hasta encontrar uno que no exista
    let newGroupCode = generateGroupCode();
    let attempts = 0;
    while (isGroupCodeDuplicate(newGroupCode) && attempts < 10) {
      newGroupCode = generateGroupCode();
      attempts++;
    }

    // Crear nueva línea con la cantidad disponible
    const newLine: DispatchByLocationLine = {
      group_code: newGroupCode,
      product_id: productId,
      qty_expected: availableQty,
      work_type: 'LABELING',
    };

    setLines([...lines, newLine]);
  };

  const removeLine = (index: number) => {
    const newLines = lines.filter((_, i) => i !== index);
    setLines(newLines);
    setValidationErrors([]);
  };

  const validateBeforeConfirm = (): boolean => {
    const errors: string[] = [];

    if (dispatchType === 'EXTRA_WORK_ORDER') {
      // Verificar que haya al menos una línea (grupo) creada
      if (lines.length === 0) {
        errors.push(t('dispatch.modal.no_groups', 'Debe crear al menos un grupo de trabajo'));
        setValidationErrors(errors);
        return false;
      }

      // Verificar que todos los productos estén incluidos
      const movementProductIds = movements.map((m) => m.product_id);
      const lineProductIds = [...new Set(lines.map((l) => l.product_id))];
      const missingProducts = movementProductIds.filter((id) => !lineProductIds.includes(id));

      if (missingProducts.length > 0) {
        errors.push(t('dispatch.modal.missing_products', `Faltan productos: ${missingProducts.join(', ')}`));
      }

      // Verificar que TODAS las unidades estén asignadas a grupos
      const availableByProduct: Record<number, number> = {};
      movements.forEach((m) => {
        availableByProduct[m.product_id] = (availableByProduct[m.product_id] || 0) + m.qty;
      });

      const requestedByProduct: Record<number, number> = {};
      lines.forEach((l) => {
        requestedByProduct[l.product_id] = (requestedByProduct[l.product_id] || 0) + l.qty_expected;
      });

      for (const productId in availableByProduct) {
        const available = availableByProduct[productId];
        const requested = requestedByProduct[productId] || 0;
        if (requested !== available) {
          const missing = available - requested;
          errors.push(
            t(
              'dispatch.modal.quantity_mismatch',
              `Producto ${productId}: faltan ${missing} unidades por asignar a grupos (${requested}/${available})`,
            ),
          );
        }
      }

      // Verificar que todos tengan group_code
      const missingGroupCodes = lines.filter((l) => !l.group_code || !l.group_code.trim());
      if (missingGroupCodes.length > 0) {
        errors.push(t('dispatch.modal.missing_group_codes', 'Todos los productos deben tener un código de grupo'));
      }

      // Verificar que no haya códigos de grupo duplicados
      const groupCodes = lines.map((l) => l.group_code?.trim().toUpperCase()).filter(Boolean);
      const uniqueGroupCodes = new Set(groupCodes);
      if (groupCodes.length !== uniqueGroupCodes.size) {
        errors.push(t('dispatch.modal.duplicate_group_codes', 'No puede haber códigos de grupo duplicados'));
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Verificar si todas las unidades están asignadas (para deshabilitar botón)
  const areAllUnitsAssigned = (): boolean => {
    if (dispatchType !== 'EXTRA_WORK_ORDER' || movements.length === 0 || lines.length === 0) {
      return false;
    }

    const availableByProduct: Record<number, number> = {};
    movements.forEach((m) => {
      availableByProduct[m.product_id] = (availableByProduct[m.product_id] || 0) + m.qty;
    });

    const requestedByProduct: Record<number, number> = {};
    lines.forEach((l) => {
      requestedByProduct[l.product_id] = (requestedByProduct[l.product_id] || 0) + l.qty_expected;
    });

    for (const productId in availableByProduct) {
      const available = availableByProduct[productId];
      const requested = requestedByProduct[productId] || 0;
      if (requested !== available) {
        return false;
      }
    }

    // Verificar que todos tengan group_code
    const missingGroupCodes = lines.filter((l) => !l.group_code || !l.group_code.trim());
    if (missingGroupCodes.length > 0) {
      return false;
    }

    // Verificar que no haya códigos de grupo duplicados
    const groupCodes = lines.map((l) => l.group_code?.trim().toUpperCase()).filter(Boolean);
    const uniqueGroupCodes = new Set(groupCodes);
    if (groupCodes.length !== uniqueGroupCodes.size) {
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!selectedLocationId || !confirmCallbackRef.current) return;

    if (dispatchType === 'EXTRA_WORK_ORDER' && !validateBeforeConfirm()) {
      return;
    }

    setIsProcessing(true);

    try {
      const result = await confirmCallbackRef.current(
        selectedLocationId,
        dispatchType,
        dispatchType === 'EXTRA_WORK_ORDER' ? lines : undefined,
      );

      if (dispatchType === 'EXTRA_WORK_ORDER' && result?.success) {
        setCurrentStep('print_labels');
      } else {
        modalRef.current?.closeModal();
      }
    } catch (error) {
      console.error('Error al despachar ubicación:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedLocation = locations.find((loc) => loc.location_id === selectedLocationId);

  // Agrupar líneas por producto para mostrar
  const linesByProduct = movements.reduce(
    (acc, movement) => {
      const productId = movement.product_id;

      // Si ya procesamos este producto, saltar
      if (acc[productId]) return acc;

      // Sumar todas las cantidades de movimientos de este producto
      const totalAvailableQty = movements.filter((m) => m.product_id === productId).reduce((sum, m) => sum + m.qty, 0);

      const productLines = lines
        .map((line, index) => ({ ...line, index }))
        .filter((line) => line.product_id === productId);

      // Mostrar todos los productos, incluso si no tienen líneas asignadas
      const totalAssigned = productLines.reduce((sum, l) => sum + l.qty_expected, 0);
      acc[productId] = {
        movement, // Usar el primer movimiento como referencia para mostrar el producto
        lines: productLines,
        totalAssigned,
        available: totalAvailableQty - totalAssigned,
      };
      return acc;
    },
    {} as Record<
      number,
      {
        movement: EntryReceiptMovement;
        lines: Array<DispatchByLocationLine & { index: number }>;
        totalAssigned: number;
        available: number;
      }
    >,
  );

  const handlePrintLabel = async (groupCode?: string | null) => {
    if (!groupCode) return;

    try {
      setPrintingGroupCode(groupCode);
      const today = new Date();
      const labelUrl = await generateReusedLabel({
        code: groupCode,
        date: format(today, 'dd/MM/yyyy'),
      });
      window.open(labelUrl, '_blank');
      setPrintedGroupCodes((prev) => {
        const next = new Set(prev);
        next.add(groupCode);
        return next;
      });
    } catch (error) {
      console.error('Error al imprimir etiqueta de grupo:', error);
    } finally {
      setPrintingGroupCode(null);
    }
  };

  return (
    <ModalCenter ref={modalRef} title={t('dispatch.modal.title', 'Despachar Ubicación')} type="info" width="xl">
      <div className="space-y-6">
        {/* Errores de validación */}
        {validationErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Paso 1: Seleccionar tipo de despacho */}
        {currentStep === 'select_type' && (
          <>
            {/* Selector de ubicación (solo si hay múltiples ubicaciones) */}
            {locations.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="location-select">
                  {t('dispatch.modal.select_location', 'Seleccionar Ubicación')} *
                </Label>
                <Select
                  value={selectedLocationId?.toString() || ''}
                  onValueChange={(value) => setSelectedLocationId(parseInt(value))}
                >
                  <SelectTrigger id="location-select">
                    <SelectValue placeholder={t('dispatch.modal.location_placeholder', 'Seleccionar ubicación...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.location_id} value={location.location_id.toString()}>
                        {location.location_code} ({location.pending_movements}{' '}
                        {t('dispatch.modal.movements', 'movimientos')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selector de tipo de despacho con cards */}
            <div className="space-y-3">
              <Label>{t('dispatch.modal.dispatch_type', 'Tipo de Despacho')} *</Label>
              <RadioGroup
                value={dispatchType}
                onValueChange={(value) => handleDispatchTypeChange(value as 'LOCATION_ORDER' | 'EXTRA_WORK_ORDER')}
                className="grid grid-cols-2 gap-3"
              >
                {/* Card Orden de Ubicación */}
                <label
                  htmlFor="dispatch-location-order"
                  className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                    dispatchType === 'LOCATION_ORDER'
                      ? 'border-primary/30 bg-muted/50'
                      : 'border-border hover:border-primary/30 bg-background'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="LOCATION_ORDER" id="dispatch-location-order" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-foreground">
                          {t('dispatch.modal.location_order', 'Orden de Ubicación')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          'dispatch.modal.location_order_description',
                          'Creará una orden para ubicar los productos en el almacén definitivo',
                        )}
                      </p>
                    </div>
                  </div>
                </label>

                {/* Card Orden de Trabajo Extra */}
                <label
                  htmlFor="dispatch-extra-work-order"
                  className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                    dispatchType === 'EXTRA_WORK_ORDER'
                      ? 'border-primary/30 bg-muted/50'
                      : 'border-border hover:border-primary/30 bg-background'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value="EXTRA_WORK_ORDER" id="dispatch-extra-work-order" className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-foreground">
                          {t('dispatch.modal.extra_work_order', 'Orden de Trabajo Extra')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {t(
                          'dispatch.modal.extra_work_order_description',
                          'Creará una orden de trabajo extra (etiquetado, reembalaje, etc.)',
                        )}
                      </p>
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Lista de productos */}
            {selectedLocation && selectedLocation.products && selectedLocation.products.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-title">
                  {t('dispatch.modal.products', 'Productos')} ({selectedLocation.products.length})
                </Label>
                <ScrollArea className="max-h-48">
                  <div className="space-y-2 pr-2">
                    {selectedLocation.products.map((product) => (
                      <div
                        key={product.product_id}
                        className="flex items-center justify-between p-3 bg-background rounded-lg border border-border"
                      >
                        <Product
                          id={product.product_id}
                          name={product.product_name}
                          src={buildImageUrl(product.product_image)}
                          code={product.product_code}
                          showImageModal={true}
                          enableLineClamp={true}
                        />
                        <span className="text-sm text-muted-foreground font-semibold ml-4">{product.qty} ud.</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </>
        )}

        {/* Paso 2: Configurar grupos (solo para EXTRA_WORK_ORDER) */}
        {currentStep === 'configure_groups' && dispatchType === 'EXTRA_WORK_ORDER' && (
          <div className="space-y-4">
            {/* Título */}
            <div>
              <p className="text-sm text-complementary-text">
                {t(
                  'dispatch.modal.configure_groups_description',
                  'Divide cada producto en grupos y asigna el tipo de trabajo',
                )}
              </p>
            </div>

            {/* Lista de productos con sus líneas */}
            {isLoadingMovements ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <div className="space-y-6 pr-2">
                  {Object.entries(linesByProduct).map(
                    ([productId, { movement, lines: productLines, totalAssigned, available }]) => {
                      const product = movement.product;
                      const totalAvailable = totalAssigned + available;
                      const progressPercentage = totalAvailable > 0 ? (totalAssigned / totalAvailable) * 100 : 0;

                      return (
                        <div key={productId} className="bg-background border border-border rounded-lg p-5">
                          {/* Header del producto */}
                          <div className="mb-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0">
                                <Product
                                  id={movement.product_id}
                                  name={product?.name || `Producto ${productId}`}
                                  src={buildImageUrl(product?.img)}
                                  code={product?.code}
                                  showImageModal={true}
                                  enableLineClamp={true}
                                />
                              </div>
                              {available > 0 && (
                                <Button
                                  onClick={() => addLineForProduct(movement.product_id)}
                                  variant="primary"
                                  size="sm"
                                  className="flex items-center gap-2 ml-4 flex-shrink-0"
                                >
                                  <Plus className="w-4 h-4" />
                                  {t('dispatch.modal.add_group', 'Añadir Grupo')}
                                </Button>
                              )}
                            </div>

                            {/* Barra de progreso visual */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-complementary-text">
                                  {t('dispatch.modal.progress', 'Progreso')}
                                </span>
                                <span
                                  className={`font-semibold ${totalAssigned === totalAvailable ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}
                                >
                                  {totalAssigned} / {totalAvailable} {t('dispatch.modal.units', 'ud.')}
                                </span>
                              </div>
                              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-300 ${
                                    totalAssigned === totalAvailable
                                      ? 'bg-green-500 dark:bg-green-600'
                                      : 'bg-orange-500 dark:bg-orange-600'
                                  }`}
                                  style={{ width: `${progressPercentage}%` }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Grupos del producto */}
                          <div className="space-y-3">
                            <div className="text-xs font-medium text-complementary-text uppercase tracking-wide mb-2">
                              {t('dispatch.modal.groups', 'Grupos de Trabajo')} ({productLines.length})
                            </div>
                            {productLines.length === 0 ? (
                              <div className="text-center py-6 text-sm text-complementary-text border border-dashed border-border rounded-lg bg-muted/20">
                                {t(
                                  'dispatch.modal.no_groups_created',
                                  'No hay grupos creados. Haz clic en "Añadir Grupo" para comenzar.',
                                )}
                              </div>
                            ) : (
                              productLines.map(({ index, group_code, qty_expected, work_type }) => (
                                <div key={index} className="bg-muted/30 rounded-lg p-4 border border-border/50">
                                  <div className="grid grid-cols-12 gap-4 items-center">
                                    {/* Código de grupo */}
                                    <div className="col-span-12 sm:col-span-4">
                                      <Label className="text-xs text-complementary-text mb-1.5 block">
                                        {t('dispatch.modal.group_code', 'Código de Grupo')}
                                      </Label>
                                      <div className="flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-primary flex-shrink-0" />
                                        <Input
                                          value={group_code}
                                          onChange={(e) => updateLine(index, 'group_code', e.target.value)}
                                          placeholder="TE-2501-1234"
                                          className="font-mono text-sm h-9"
                                        />
                                      </div>
                                    </div>

                                    {/* Cantidad */}
                                    <div className="col-span-6 sm:col-span-3">
                                      <Label className="text-xs text-complementary-text mb-1.5 block">
                                        {t('dispatch.modal.quantity', 'Cantidad')}
                                      </Label>
                                      <Input
                                        type="number"
                                        min={1}
                                        max={totalAvailable}
                                        value={qty_expected}
                                        onChange={(e) => {
                                          const newQty = parseInt(e.target.value) || 0;
                                          const maxQty =
                                            totalAvailable -
                                            productLines
                                              .filter((l) => l.index !== index)
                                              .reduce((sum, l) => sum + l.qty_expected, 0);
                                          updateLine(index, 'qty_expected', Math.min(Math.max(newQty, 1), maxQty));
                                        }}
                                        className="text-center h-9"
                                      />
                                    </div>

                                    {/* Tipo de trabajo */}
                                    <div className="col-span-6 sm:col-span-4">
                                      <Label className="text-xs text-complementary-text mb-1.5 block">
                                        {t('dispatch.modal.work_type_label', 'Tipo de Trabajo')}
                                      </Label>
                                      <Select
                                        value={work_type}
                                        onValueChange={(value) => updateLine(index, 'work_type', value)}
                                      >
                                        <SelectTrigger className="h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="LABELING">
                                            {t('dispatch.modal.work_type.labeling', 'Etiquetado')}
                                          </SelectItem>
                                          <SelectItem value="BAGGING">
                                            {t('dispatch.modal.work_type.bagging', 'Embolsado')}
                                          </SelectItem>
                                          <SelectItem value="BOTH">
                                            {t('dispatch.modal.work_type.both', 'Ambos')}
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Botón eliminar */}
                                    <div className="col-span-12 sm:col-span-1 flex justify-end sm:justify-center">
                                      {productLines.length > 1 ? (
                                        <Button
                                          onClick={() => removeLine(index)}
                                          variant="outline"
                                          size="sm"
                                          className="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      ) : (
                                        <div className="w-9" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paso 3: Imprimir etiquetas de grupos (solo para EXTRA_WORK_ORDER) */}
        {currentStep === 'print_labels' && dispatchType === 'EXTRA_WORK_ORDER' && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-title">
                {t(
                  'dispatch.modal.labels_step_success',
                  'El trabajo extra se creó correctamente. Ahora puedes imprimir las etiquetas de los grupos.',
                )}
              </p>
              <p className="text-sm text-title mt-1">
                {t(
                  'dispatch.modal.labels_step_description',
                  'Descarga e imprime una etiqueta por cada grupo para identificar los trabajos extra.',
                )}
              </p>
            </div>

            <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
              <div className="space-y-4 pr-2">
                {Object.entries(linesByProduct).map(([productId, { movement, lines: productLines }]) => {
                  const product = movement.product;

                  return (
                    <div key={productId} className="bg-card-bg border border-card-border rounded-lg p-4">
                      {productLines.map(({ index, group_code, qty_expected, work_type }) => {
                        const productImageUrl = buildImageUrl(product?.img);

                        return (
                          <div
                            key={index}
                            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-card-border last:border-b-0"
                          >
                            {/* Datos del grupo y producto en una sola fila */}
                            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 min-w-0">
                              <div className="space-y-1 min-w-0">
                                <div className="text-sm font-semibold text-title">{group_code}</div>
                                <div className="flex flex-wrap gap-4 text-xs text-complementary-text items-center">
                                  {/* Producto con avatar circular - abre modal propia */}
                                  {!!product?.name && !!productImageUrl && (
                                    <button
                                      type="button"
                                      className="flex items-center gap-1.5 group"
                                      onClick={() => {
                                        setPreviewImageUrl(productImageUrl);
                                        setPreviewImageName(product.name);
                                      }}
                                    >
                                      <span className="w-6 h-6 rounded-full overflow-hidden border border-card-border flex-shrink-0">
                                        <img
                                          src={productImageUrl}
                                          alt={product.name}
                                          className="w-full h-full object-cover"
                                        />
                                      </span>
                                      <span className="truncate max-w-[140px] sm:max-w-[220px] group-hover:underline">
                                        {product.name}
                                      </span>
                                    </button>
                                  )}

                                  {/* Cantidad con icono de caja */}
                                  <span className="flex items-center gap-1.5">
                                    <FontAwesomeIcon icon={faBox} className="w-3 h-3" />
                                    <span className="font-semibold">{qty_expected}</span>
                                  </span>

                                  {/* Tipo de trabajo con icono de tag (igual que ExtraWorkOrders) */}
                                  <span className="flex items-center gap-1.5">
                                    <FontAwesomeIcon icon={faTagIcon} className="w-3 h-3" />
                                    <span>
                                      {work_type === 'LABELING' && t('dispatch.modal.work_type.labeling', 'Etiquetado')}
                                      {work_type === 'BAGGING' && t('dispatch.modal.work_type.bagging', 'Embolsado')}
                                      {work_type === 'BOTH' && t('dispatch.modal.work_type.both', 'Ambos')}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Botón de imprimir etiqueta */}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 flex-shrink-0 self-start sm:self-auto"
                              onClick={() => handlePrintLabel(group_code)}
                            >
                              {printingGroupCode === group_code ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                              ) : printedGroupCodes.has(group_code || '') ? (
                                <>
                                  <Check className="w-4 h-4 text-success" />
                                  <span className="text-xs font-medium text-success">
                                    {t('dispatch.modal.print_label', 'Imprimir etiqueta')}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Printer className="w-4 h-4" />
                                  <span className="text-xs font-medium">
                                    {t('dispatch.modal.print_label', 'Imprimir etiqueta')}
                                  </span>
                                </>
                              )}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        {currentStep === 'print_labels' ? (
          <div className="flex justify-end pt-4">
            <Button type="button" variant="primary" size="lg" onClick={() => modalRef.current?.closeModal()}>
              {t('dispatch.modal.close', 'Cerrar')}
            </Button>
          </div>
        ) : (
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={
                currentStep === 'configure_groups' ? handleBackToSelectType : () => modalRef.current?.closeModal()
              }
              className="flex-1"
              disabled={isProcessing}
            >
              {currentStep === 'configure_groups' ? (
                <>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('dispatch.modal.back', 'Volver')}
                </>
              ) : (
                t('dispatch.modal.cancel', 'Cancelar')
              )}
            </Button>
            {currentStep === 'select_type' ? (
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={handleNextStep}
                className="flex-1"
                disabled={!selectedLocationId || isProcessing}
              >
                {dispatchType === 'EXTRA_WORK_ORDER'
                  ? t('dispatch.modal.next', 'Siguiente')
                  : t('dispatch.modal.confirm', 'Confirmar Despacho')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={handleConfirm}
                className="flex-1"
                disabled={isProcessing || !areAllUnitsAssigned()}
              >
                {isProcessing
                  ? t('dispatch.modal.processing', 'Procesando...')
                  : t('dispatch.modal.confirm', 'Confirmar Despacho')}
              </Button>
            )}
          </div>
        )}

        {/* Modal simple para previsualizar imagen de producto */}
        {previewImageUrl && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
            <div className="bg-body-bg border border-card-border rounded-lg max-w-[80vw] max-h-[80vh] p-4 shadow-xl">
              <div className="flex items-center justify-between mb-3 gap-4">
                <div className="text-sm font-medium text-title truncate">{previewImageName}</div>
                <button
                  type="button"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-card-border text-complementary-text hover:bg-hover-press hover:text-text transition-colors"
                  onClick={() => {
                    setPreviewImageUrl(null);
                    setPreviewImageName(null);
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="max-w-full max-h-[70vh] overflow-hidden">
                <img
                  src={previewImageUrl}
                  alt={previewImageName || ''}
                  className="max-w-full max-h-[70vh] object-contain rounded-md"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

DispatchModal.displayName = 'DispatchModal';
